import React, { useEffect, useState } from "react";
import Main from "layouts/main";
import { ChatbotStoreProvider } from "context/chatbot-state";
import Display from "./display";
import { useTheme, Box } from "@mui/material";
import TwoColumnCard from "../../blocks/TwoColumnCard";
import { BotHostContainer } from "views/components";
import { GridContainer, Image } from "components";
import { Loading } from "components";
import NotFound from "views/not-found";
import { parseAdditionalSlots } from "utils/helper-generic";
import JumboCardAlt from "blocks/JumboCardAlt";
import { createBotPayload } from "utils/helper-generic";

const Weddings = (): JSX.Element => {
  const { palette } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [botPayload, setBotPayload] = useState<{
    payload: any;
    botId: string;
    botType: string;
  } | null>(null);

  useEffect(() => {
    const fetchProductBySlug = async () => {
      setLoading(true);
      try {
        const res = await (
          await fetch(
            `${process.env.REACT_APP_OPS_API_HOST}/fetch-product-by-slug`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ slug: "wedding" }),
            }
          )
        ).json();

        const fetchedEvent = res.product;

        // Check required properties before proceeding
        const requiredProperties = [
          "package_config",
          "mandate_liability",
          "mandate_cancellation",
          "slug",
          "bot",
        ];

        const missingProperties = requiredProperties.filter(
          (prop) => fetchedEvent[prop] === undefined
        );

        if (missingProperties.length > 0) {
          console.error(
            `Missing required event properties: ${missingProperties.join(", ")}`
          );
          setEvent(null);
        } else {
          setEvent(fetchedEvent);

          let data: any = {
            package_config: fetchedEvent.package_config,
            mandate_liability: fetchedEvent.mandate_liability,
            mandate_cancellation: fetchedEvent.mandate_cancellation,
            path_type: "product",
            product: fetchedEvent.slug,
            cancellation_available: true,
            ...parseAdditionalSlots(fetchedEvent.additional_slots),
          };

          let botPayload = createBotPayload(
            data,
            fetchedEvent.bot,
            fetchedEvent.first_message
          );
          setBotPayload(botPayload);
        }

        // update page title
        document.title = fetchedEvent.page_title;
        // update page description
        const metaDescription = document.querySelector(
          'meta[name="description"]'
        );
        if (metaDescription) {
          metaDescription.setAttribute(
            "content",
            fetchedEvent.page_meta_description
          );
        }
      } catch (err) {
        console.error(err);
        setEvent(null);
      }

      setLoading(false);
    };

    fetchProductBySlug();
  }, []);

  if (loading || !botPayload) {
    return <Loading loading={true} />;
  }

  if (!event) {
    return <NotFound />;
  }

  const { payload, botId, botType } = botPayload;

  return (
    <ChatbotStoreProvider
      botType={botType}
      initPayload={payload}
      botId={botId}
      customData={event.bot === "wedding" ? {} : payload}
    >
      <Main>
        {/* Debug element - can be removed in production */}
        <div
          id="debug-payload"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "rgba(0,0,0,0.8)",
            color: "white",
            padding: "10px",
            maxWidth: "300px",
            maxHeight: "200px",
            overflow: "auto",
            fontSize: "12px",
            zIndex: 9999,
            display: "none", // Set to 'block' to show debug info
          }}
        ></div>

        {/* hero background */}
        <Image
          src="https://res.cloudinary.com/deksgbacz/image/upload/v1698113613/wedding_hero_vibrant_v6_awyqnx.png"
          sx={{
            display: "flex",
            background: palette.background.directInsuranceHero,
            clipPath: "url(#myClip)",
            height: { xs: "70vh", md: "50vh", xl: "unset" },
          }}
          imageProps={{
            style: {
              objectFit: "cover",
              mixBlendMode: "overlay",
            },
          }}
        />

        <svg width="0" height="0">
          <defs>
            <clipPath id="myClip" clipPathUnits="objectBoundingBox">
              <path
                transform="scale(0.00069444444, 0.00202839756)"
                d="M1440 0H0V438.385C493.657 513.754 891.5 507 1440 438.385V0Z"
              />
            </clipPath>
          </defs>
        </svg>

        <Box
          sx={{
            background:
              "radial-gradient(68.79% 89.54% at 114.1% -18.98%, rgba(0, 99, 247, 0.20) 0%, rgba(85, 133, 255, 0.20) 50.52%, rgba(245, 249, 255, 0.20) 100%), linear-gradient(326deg, rgba(255, 255, 255, 0.88) 48.8%, rgba(220, 219, 254, 0.88) 99.98%)",
            mt: { xs: "-30vh", md: "-12vh" },
          }}
        >
          <GridContainer>
            <TwoColumnCard
              title="Wedding Insurance Coverage"
              text="Protect your special day with comprehensive coverage"
            />
            <Display />
            <JumboCardAlt
              title="Your policy quality matters"
              text="The quality of your event insurance coverage is our top priority"
            />
            <BotHostContainer />
          </GridContainer>
        </Box>
      </Main>
    </ChatbotStoreProvider>
  );
};

export default Weddings;
